import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Layout from "./Layout";
import { useSelector } from "react-redux";
import { getTrustCenterFrameworksEmployeeApi } from "../services/axios/AuthApis/TrustCenterApis";

const ProtectedRoute = ({ children, moduleName = null, requiredRole = null, requiredRoles = null }) => {
  const { isAuthenticated, loading, user, canAccess } = useAuth();
  const [checkingTrustCenterAccess, setCheckingTrustCenterAccess] = useState(false);
  const [hasEndUserTrustCenterAccess, setHasEndUserTrustCenterAccess] = useState(false);

  useEffect(() => {
    const resolveEndUserTrustCenterAccess = async () => {
      if (!(moduleName === "trust-center" && user?.role === "end_user" && user?.id)) {
        setHasEndUserTrustCenterAccess(false);
        setCheckingTrustCenterAccess(false);
        return;
      }

      setCheckingTrustCenterAccess(true);
      try {
        const response = await getTrustCenterFrameworksEmployeeApi(user.id);
        const hasAssignments =
          (Array.isArray(response?.results) && response.results.length > 0) ||
          Number(response?.count || 0) > 0;

        setHasEndUserTrustCenterAccess(hasAssignments);
      } catch (error) {
        setHasEndUserTrustCenterAccess(false);
      } finally {
        setCheckingTrustCenterAccess(false);
      }
    };

    resolveEndUserTrustCenterAccess();
  }, [moduleName, user?.id, user?.role]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const hasModuleAccess = moduleName
    ? (moduleName === "trust-center" && user?.role === "end_user"
      ? hasEndUserTrustCenterAccess
      : canAccess(moduleName))
    : true;

  const isCheckingTrustCenter =
  moduleName === "trust-center" && user?.role === "end_user" && checkingTrustCenterAccess;

  const showAccessDenied = moduleName && !hasModuleAccess && !isCheckingTrustCenter;

  if (showAccessDenied) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-4">You don't have permission to access this module.</p>
            <p className="text-sm text-gray-500 mb-6">Contact your administrator to request access.</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Fallback to role-based access (for backward compatibility)
  const hasRequiredRole = () => {
    if (requiredRoles) {
      return requiredRoles.includes(user?.role);
    }
    if (requiredRole) {
      return user?.role === requiredRole;
    }
    return true; // No role requirement
  };

  if ((requiredRole || requiredRoles) && !hasRequiredRole()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">You don't have the required role to access this page.</p>
          <p className="text-sm text-gray-500 mb-6">Required role: {requiredRole || requiredRoles?.join(', ')}</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      {children}
    </Layout>
  );
};

export default ProtectedRoute;